import { RaydiumPositionExpanded } from "@bridgesplit/abf-sdk";
import { retryAsync, RPC_URL } from "@bridgesplit/utils";
import { Connection, PublicKey } from "@solana/web3.js";
import { LiquidityMath, Raydium, SqrtPriceMath } from "@raydium-io/raydium-sdk-v2";
import BN from "bn.js";
import { RaydiumPoolData } from "@bridgesplit/abf-react";

import { AddLiquidityQuote, DecreaseLiquidityQuote, OpenPositionQuote } from "./types";
import { AUTO_SLIPPAGE_DECIMALS } from "./RaydiumContext";

/**
 * Returns the minimum amounts of tokenA and tokenB that would be received
 * when removing all liquidity from a Raydium position, accounting for slippage.
 */

const connection = new Connection(RPC_URL, "processed");
let raydium: Awaited<ReturnType<typeof Raydium.load>> | null = null;
let currentOwner: string | null = null;

export async function initRaydium(owner: string) {
    const ownerPubkey = new PublicKey(owner);
    if (!raydium || currentOwner !== ownerPubkey.toBase58()) {
        raydium = await Raydium.load({
            connection,
            owner: ownerPubkey
        });
        currentOwner = ownerPubkey.toBase58();
    }
    return raydium;
}

export async function getPoolData(address: string, owner: string | undefined) {
    try {
        if (!owner) throw new Error("Owner not found");
        const raydiumInstance = await initRaydium(owner);
        const pool = await raydiumInstance.clmm.getRpcClmmPoolInfo({ poolId: address });
        if (!pool) throw new Error("Pool not found");
        return pool;
    } catch (error) {
        return null;
    }
}

export async function getPositionData(raydiumPosition: RaydiumPositionExpanded, owner: string | undefined) {
    try {
        return await retryAsync(async () => {
            if (!owner) throw new Error("Owner not found");
            const raydiumInstance = await initRaydium(owner);
            const positionDataRaw = await raydiumInstance.clmm.getOwnerPositionInfo({});

            if (!positionDataRaw) throw new Error("Position not found");
            const positionData = positionDataRaw[0];
            if (!positionData) throw new Error("Position not found");

            const quote = getRemoveLiquidityQuote({
                position: raydiumPosition,
                liquidity: raydiumPosition.position.liquidity,
                slippage: AUTO_SLIPPAGE_DECIMALS
            });

            return { positionData, quote };
        }, 3);
    } catch (error) {
        return null;
    }
}

export enum PositionStatus {
    BelowRange,
    InRange,
    AboveRange
}

function getPositionStatus(tickCurrent: number, tickLower: number, tickUpper: number): PositionStatus {
    if (tickCurrent < tickLower) return PositionStatus.BelowRange;
    if (tickCurrent >= tickUpper) return PositionStatus.AboveRange;
    return PositionStatus.InRange;
}

export type AddLiquidityQuoteParam = {
    positionAddress: string;
    tokenMint: string;
    tokenAmount: BN;
    refresh: boolean;
    slippageTolerance?: number; // e.g. 0.01 = 1%
};

export function getAddLiquidityQuote(
    position: RaydiumPositionExpanded,
    params: AddLiquidityQuoteParam
): AddLiquidityQuote {
    const { tokenMint, tokenAmount, slippageTolerance } = params;
    const { tokenA, tokenB } = position;
    const { tickLowerIndex, tickUpperIndex } = position.position;
    const { sqrtPriceX64, tickCurrent } = position.pool;

    const lowerSqrtPriceX64 = SqrtPriceMath.getSqrtPriceX64FromTick(tickLowerIndex);
    const upperSqrtPriceX64 = SqrtPriceMath.getSqrtPriceX64FromTick(tickUpperIndex);

    const status = getPositionStatus(tickCurrent, tickLowerIndex, tickUpperIndex);
    const roundUp = true;

    let liquidity = new BN(0);
    let estTokenA = new BN(0);
    let estTokenB = new BN(0);

    if (status === PositionStatus.BelowRange) {
        // Only token A can be deposited
        if (tokenMint !== tokenA.mint) {
            return {
                positionAddress: params.positionAddress,
                estTokenA,
                estTokenB,
                liquidity,
                maxTokenA: estTokenA,
                maxTokenB: estTokenB,
                isTokenA: true, // true if tokenA is the one being added
                amount: 0 // no token A added
            };
        }

        liquidity = LiquidityMath.getLiquidityFromTokenAmountA(
            lowerSqrtPriceX64,
            upperSqrtPriceX64,
            tokenAmount,
            roundUp
        );

        ({ amountA: estTokenA, amountB: estTokenB } = LiquidityMath.getAmountsFromLiquidity(
            sqrtPriceX64,
            lowerSqrtPriceX64,
            upperSqrtPriceX64,
            liquidity,
            roundUp
        ));
    } else if (status === PositionStatus.AboveRange) {
        // Only token B can be deposited
        if (tokenMint !== tokenB.mint) {
            return {
                positionAddress: params.positionAddress,
                estTokenA,
                estTokenB,
                liquidity,
                maxTokenA: estTokenA,
                maxTokenB: estTokenB,
                isTokenA: false,
                amount: 0 // no token B added
            };
        }

        liquidity = LiquidityMath.getLiquidityFromTokenAmountB(lowerSqrtPriceX64, upperSqrtPriceX64, tokenAmount);

        ({ amountA: estTokenA, amountB: estTokenB } = LiquidityMath.getAmountsFromLiquidity(
            sqrtPriceX64,
            lowerSqrtPriceX64,
            upperSqrtPriceX64,
            liquidity,
            roundUp
        ));
    } else {
        // In range: both sides may be used
        if (tokenMint === tokenA.mint) {
            liquidity = LiquidityMath.getLiquidityFromTokenAmountA(
                sqrtPriceX64,
                upperSqrtPriceX64,
                tokenAmount,
                roundUp
            );
        } else if (tokenMint === tokenB.mint) {
            liquidity = LiquidityMath.getLiquidityFromTokenAmountB(lowerSqrtPriceX64, sqrtPriceX64, tokenAmount);
        } else {
            throw new Error("Provided tokenMint is not part of the pool");
        }

        ({ amountA: estTokenA, amountB: estTokenB } = LiquidityMath.getAmountsFromLiquidity(
            sqrtPriceX64,
            lowerSqrtPriceX64,
            upperSqrtPriceX64,
            liquidity,
            roundUp
        ));
    }

    const { amountSlippageA: maxTokenA, amountSlippageB: maxTokenB } =
        LiquidityMath.getAmountsFromLiquidityWithSlippage(
            sqrtPriceX64,
            lowerSqrtPriceX64,
            upperSqrtPriceX64,
            liquidity,
            true, // amountMax
            roundUp,
            slippageTolerance ?? 0.01
        );

    return {
        positionAddress: params.positionAddress,
        estTokenA,
        estTokenB,
        liquidity,
        maxTokenA,
        maxTokenB,
        amount: tokenAmount.toNumber(),
        isTokenA: tokenMint === tokenA.mint
    };
}

export function getRemoveLiquidityQuote({
    position,
    liquidity,
    slippage
}: {
    position: RaydiumPositionExpanded;
    liquidity: BN;
    slippage: number;
}): DecreaseLiquidityQuote {
    // Step 1: compute expected amounts
    const { tickLowerIndex, tickUpperIndex } = position.position;
    const { sqrtPriceX64, tickCurrent } = position.pool;

    const lowerSqrtPriceX64 = SqrtPriceMath.getSqrtPriceX64FromTick(tickLowerIndex);
    const upperSqrtPriceX64 = SqrtPriceMath.getSqrtPriceX64FromTick(tickUpperIndex);

    const { amountA: estAmountA, amountB: estAmountB } = LiquidityMath.getAmountsFromLiquidity(
        sqrtPriceX64,
        lowerSqrtPriceX64,
        upperSqrtPriceX64,
        liquidity,
        false
    );

    // Step 2: compute min amounts w/ slippage (Raydium handles both tokens at once). We round down to avoid issues with slippage.
    const { amountSlippageA, amountSlippageB } = LiquidityMath.getAmountsFromLiquidityWithSlippage(
        sqrtPriceX64,
        lowerSqrtPriceX64,
        upperSqrtPriceX64,
        liquidity,
        false, // amountMax = false
        false, // roundUp = false
        slippage
    );

    const status = getPositionStatus(tickCurrent, tickLowerIndex, tickUpperIndex);

    let estTokenA = new BN(0);
    let estTokenB = new BN(0);
    let minTokenA = new BN(0);
    let minTokenB = new BN(0);

    if (status === PositionStatus.BelowRange) {
        estTokenA = estAmountA;
        minTokenA = amountSlippageA;
    } else if (status === PositionStatus.AboveRange) {
        estTokenB = estAmountB;
        minTokenB = amountSlippageB;
    } else {
        estTokenA = estAmountA;
        estTokenB = estAmountB;
        minTokenA = amountSlippageA;
        minTokenB = amountSlippageB;
    }

    const isMainlyTokenA = estAmountA.gt(estAmountB);
    return {
        positionAddress: position.position.nftMint,
        estTokenA,
        estTokenB,
        minTokenA,
        minTokenB,
        liquidity,
        isTokenA: isMainlyTokenA,
        amount: isMainlyTokenA ? estAmountA.toNumber() : estAmountB.toNumber()
    };
}

function adjustForSlippage(n: BN, slippage: number, adjustUp: boolean): BN {
    // slippage is a fraction (0.01 = 1%)
    const numerator = new BN(Math.floor(slippage * 10_000)); // bps
    const denominator = new BN(10_000);

    if (adjustUp) {
        return n.mul(denominator.add(numerator)).div(denominator);
    } else {
        return n.mul(denominator).div(denominator.add(numerator));
    }
}

export function getOpenPositionQuote({
    position,
    poolData,
    tokenMint,
    tokenAmount,
    tickLowerIndex,
    tickUpperIndex,
    slippageTolerance = 0.01 // default 1%
}: {
    position: RaydiumPositionExpanded;
    poolData: RaydiumPoolData;
    tokenMint: string;
    tokenAmount: BN;
    tickLowerIndex: number;
    tickUpperIndex: number;
    slippageTolerance?: number;
}): OpenPositionQuote {
    const { tokenA, tokenB } = position;

    const sqrtPriceCurrentX64 = poolData.sqrtPriceX64;
    const tickCurrentIndex = poolData.tickCurrent;

    const sqrtPriceLowerX64 = SqrtPriceMath.getSqrtPriceX64FromTick(tickLowerIndex);
    const sqrtPriceUpperX64 = SqrtPriceMath.getSqrtPriceX64FromTick(tickUpperIndex);

    const status = getPositionStatus(tickCurrentIndex, tickLowerIndex, tickUpperIndex);

    let estTokenA = new BN(0);
    let estTokenB = new BN(0);
    let maxTokenA = new BN(0);
    let maxTokenB = new BN(0);
    let liquidity = new BN(0);

    switch (status) {
        case PositionStatus.BelowRange: {
            // Only token A is required
            if (tokenMint !== tokenA.mint) break;

            liquidity = LiquidityMath.getLiquidityFromTokenAmountA(
                sqrtPriceLowerX64,
                sqrtPriceUpperX64,
                tokenAmount,
                false
            );

            estTokenA = LiquidityMath.getTokenAmountAFromLiquidity(
                sqrtPriceLowerX64,
                sqrtPriceUpperX64,
                liquidity,
                true
            );
            maxTokenA = adjustForSlippage(estTokenA, slippageTolerance, true);
            break;
        }

        case PositionStatus.AboveRange: {
            // Only token B is required
            if (tokenMint !== tokenB.mint) break;

            liquidity = LiquidityMath.getLiquidityFromTokenAmountB(sqrtPriceLowerX64, sqrtPriceUpperX64, tokenAmount);

            estTokenB = LiquidityMath.getTokenAmountBFromLiquidity(
                sqrtPriceLowerX64,
                sqrtPriceUpperX64,
                liquidity,
                true
            );
            maxTokenB = adjustForSlippage(estTokenB, slippageTolerance, true);
            break;
        }

        case PositionStatus.InRange: {
            const isTokenA = tokenMint === tokenA.mint;
            if (isTokenA) {
                liquidity = LiquidityMath.getLiquidityFromTokenAmountA(
                    sqrtPriceCurrentX64,
                    sqrtPriceUpperX64,
                    tokenAmount,
                    false
                );
            } else {
                liquidity = LiquidityMath.getLiquidityFromTokenAmountB(
                    sqrtPriceLowerX64,
                    sqrtPriceCurrentX64,
                    tokenAmount
                );
            }

            const { amountA, amountB } = LiquidityMath.getAmountsFromLiquidity(
                sqrtPriceCurrentX64,
                sqrtPriceLowerX64,
                sqrtPriceUpperX64,
                liquidity,
                true
            );

            estTokenA = amountA;
            estTokenB = amountB;
            maxTokenA = adjustForSlippage(estTokenA, slippageTolerance, true);
            maxTokenB = adjustForSlippage(estTokenB, slippageTolerance, true);
            break;
        }
    }

    return {
        poolAddress: position.poolAddress,
        tickLowerIndex,
        tickUpperIndex,
        estTokenA,
        estTokenB,
        maxTokenA,
        maxTokenB,
        liquidity
    };
}
